import * as React from "react"

import { transformString } from "../utils/transformString"
import { LayerProps, Layer } from "./Layer"
import { SVGRoot } from "./SVGRoot"
import { Rect } from "../types/Rect"
import { transformValues } from "../utils/createTransformValues"
import { RenderTarget } from "../types/RenderEnvironment"
import { RenderEnvironment } from "../types/RenderEnvironment"
import { safeWindow } from "../../utils/safeWindow"

/**
 * @alpha
 */
export interface VectorGroupProps {
    name?: string
    opacity?: number | string
    visible: boolean
    x: number
    y: number
    rotation: number
    width: number
    height: number
    targetName?: string
    defaultName: string
    isRootVectorNode: boolean
    frame: Rect
    includeTransform?: boolean
}
/**
 * @alpha
 */
export interface VectorGroupProperties extends VectorGroupProps, LayerProps {}

/**
 * @internal
 */
export class VectorGroup extends Layer<VectorGroupProperties, {}> {
    static defaultVectorGroupProps: VectorGroupProps = {
        name: undefined,
        opacity: undefined,
        visible: true,
        x: 0,
        y: 0,
        rotation: 0,
        width: 100,
        height: 100,
        targetName: undefined,
        defaultName: "",
        isRootVectorNode: false,
        includeTransform: undefined,
        frame: { x: 0, y: 0, width: 100, height: 100 },
    }

    static readonly defaultProps: VectorGroupProperties = {
        ...Layer.defaultProps,
        ...VectorGroup.defaultVectorGroupProps,
    }

    render(): React.ReactElement<any> | null {
        if (process.env.NODE_ENV !== "production" && safeWindow["perf"]) safeWindow["perf"].nodeRender()

        const {
            id,
            name: nameProp,
            opacity,
            visible,
            targetName,
            defaultName,
            children,
            includeTransform,
            x,
            y,
            width,
            height,
            rotation,
            isRootVectorNode,
        } = this.props

        if (!visible) return null
        const { target } = RenderEnvironment

        const rect = { x, y, width, height }
        const transform = transformValues(rect, rotation, isRootVectorNode, includeTransform)

        const addNames = target === RenderTarget.preview
        let name: string | undefined = undefined
        if (addNames) {
            if (targetName) {
                name = targetName
            } else if (nameProp) {
                name = nameProp
            } else {
                name = defaultName
            }
        }

        return this.renderElement(
            <g transform={transformString(transform)} {...{ id, name, opacity }}>
                {children}
            </g>
        )
    }

    private renderElement(element: JSX.Element) {
        const { isRootVectorNode, width, height, frame, willChangeTransform } = this.props

        if (!isRootVectorNode) {
            return element
        } // else

        return <SVGRoot {...{ frame, width, height, willChangeTransform }}>{element}</SVGRoot>
    }
}
